import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Heart, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const Login = () => {
  const { login, loginWithGoogle, isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const from = location.state?.from?.pathname || '/feed';

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success(t('success'));
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-100 flex">
      {/* Left Panel - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1739757646223-aae94c6ca541?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Community"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/80 to-emerald-900/60" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center">
            <Heart className="h-16 w-16 text-white mx-auto mb-6" fill="currentColor" />
            <h1 className="font-heading text-4xl font-bold text-white mb-4">Bentornato su Moneyfor</h1>
            <p className="text-emerald-100 text-lg max-w-md">
              La tua generosità sta cambiando vite. Continua a fare la differenza.
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <Link to="/" className="flex items-center gap-2">
              <Heart className="h-8 w-8 text-emerald-800" fill="currentColor" />
              <span className="font-heading text-2xl font-bold text-stone-900">Moneyfor</span>
            </Link>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8">
            <h2 className="font-heading text-2xl font-semibold text-stone-900 mb-2">{t('login')}</h2>
            <p className="text-stone-600 mb-8">Inserisci le tue credenziali per accedere</p>

            {/* Google Login */}
            <Button
              data-testid="google-login-btn"
              type="button"
              variant="outline"
              className="w-full h-12 rounded-xl border-stone-200 hover:bg-stone-50 mb-6"
              onClick={loginWithGoogle}
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('loginWithGoogle')}
            </Button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-stone-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-stone-500">oppure</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">{t('email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    placeholder="nome@email.com"
                    className="pl-10 h-12 rounded-xl border-stone-200 bg-stone-50 focus:bg-white"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
                  <Input
                    id="password"
                    data-testid="password-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="pl-10 pr-10 h-12 rounded-xl border-stone-200 bg-stone-50 focus:bg-white"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="login-submit-btn"
                disabled={loading}
                className="w-full h-12 bg-emerald-800 hover:bg-emerald-900 text-white rounded-xl font-semibold"
              >
                {loading ? t('loading') : t('login')}
              </Button>
              
              <div className="text-right mt-2">
                <Link to="/forgot-password" className="text-sm text-amber-700 hover:text-amber-800 hover:underline" data-testid="forgot-password-link">
                  {language === 'it' ? 'Password dimenticata?' : 'Forgot password?'}
                </Link>
              </div>
            </form>

            <p className="mt-6 text-center text-stone-600">
              {t('noAccount')}{' '}
              <Link to="/register" className="text-emerald-800 hover:underline font-medium">
                {t('register')}
              </Link>
            </p>
          </div>

          <div className="mt-6 text-center">
            <Link to="/" className="text-stone-500 hover:text-stone-700 text-sm">
              ← Torna alla home
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
